import React from "react";
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Col, Row, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { mostUsedPages } from '../pageUsage';
import { groupOfPage, NAV_GROUPS, visibleNavGroups } from '../navbar/navItems';

const { Title, Text } = Typography;

const logoModule = require('../../assets/logo.svg');
const logoUrl = typeof logoModule === 'string' ? logoModule : (logoModule?.default ?? '');

const ACCENT = '#7b6ef6';

const groupLabel = (pageKey) => NAV_GROUPS.find((g) => g.key === groupOfPage(pageKey))?.label;

const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

const firstName = (user) => {
    const name = user?.displayName || user?.email || '';
    return name.split(/[\s@]/)[0] || 'there';
};

const cardStyle = {
    display: 'block',
    height: '100%',
    padding: 16,
    borderRadius: 12,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'inherit',
    transition: 'border-color 0.15s, background 0.15s',
};

/** A page card with its icon in a tinted tile. */
const PageCard = ({ page, hint }) => (
    <Link to={page.key} className="dashboard-card" style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span
                style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: 'rgba(123,110,246,0.16)',
                    color: ACCENT,
                    fontSize: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {page.icon}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.88)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {page.label}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>{hint}</Text>
            </div>
            <ArrowRightOutlined style={{ color: 'rgba(255,255,255,0.3)' }} />
        </div>
    </Link>
);

export const Dashboard = inject('currentUserStore')(observer(({ currentUserStore }) => {
    const user = currentUserStore.user;
    const mostUsed = mostUsedPages(user?.uid).filter(({ page }) => !page.scope || currentUserStore.can(page.scope, page.ownership));
    const groups = visibleNavGroups(currentUserStore).filter((g) => g.children);
    const today = new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

    return (
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 32px 48px' }}>
            <style>{`.dashboard-card:hover { border-color: rgba(123,110,246,0.6) !important; background: rgba(123,110,246,0.08) !important; }`}</style>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 24,
                    flexWrap: 'wrap',
                    padding: '32px 36px',
                    borderRadius: 16,
                    background: 'linear-gradient(135deg, rgba(123,110,246,0.28) 0%, rgba(123,110,246,0.06) 60%, rgba(255,255,255,0.02) 100%)',
                    border: '1px solid rgba(123,110,246,0.25)',
                }}
            >
                <div>
                    <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, letterSpacing: 0.4, textTransform: 'uppercase' }}>{today}</Text>
                    <Title level={2} style={{ margin: '6px 0 4px' }}>
                        {greeting()}, {firstName(user)}
                    </Title>
                    <Text type="secondary">Admin Central for the Design With Friends catalog, challenges and community.</Text>
                </div>
                <img src={logoUrl} alt="Design With Friends" style={{ height: 48, opacity: 0.95 }} />
            </div>

            {mostUsed.length > 0 && (
                <>
                    <Title level={4} style={{ marginTop: 36, marginBottom: 14 }}>Your most used pages</Title>
                    <Row gutter={[16, 16]}>
                        {mostUsed.map(({ page, count }) => (
                            <Col key={page.key} xs={24} sm={12} lg={8} xl={6}>
                                <PageCard page={page} hint={`${groupLabel(page.key)} · opened ${count} time${count === 1 ? '' : 's'}`} />
                            </Col>
                        ))}
                    </Row>
                </>
            )}

            <Title level={4} style={{ marginTop: 36, marginBottom: 14 }}>Everything you can manage</Title>
            <Row gutter={[16, 16]}>
                {groups.map((group) => (
                    <Col key={group.key} xs={24} md={12} xl={8}>
                        <div style={{ ...cardStyle, padding: '18px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: ACCENT, fontSize: 16 }}>
                                {group.icon}
                                <span style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 600, fontSize: 14, letterSpacing: 0.2 }}>{group.label}</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {group.children.map((page) => (
                                    <Link
                                        key={page.key}
                                        to={page.key}
                                        className="dashboard-link"
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 8, color: 'rgba(255,255,255,0.72)' }}
                                    >
                                        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, lineHeight: 0 }}>{page.icon}</span>
                                        <span style={{ fontWeight: page.bold ? 600 : 400 }}>{page.label}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
            <style>{`.dashboard-link:hover { background: rgba(255,255,255,0.06); color: #fff !important; }`}</style>
        </div>
    );
}));
